import { type ReactNode, useCallback, useState } from "react";
import { View, type LayoutChangeEvent, type ViewStyle } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import type { MediaCarouselItem, MediaCarouselProps, MediaCarouselNavDirection } from "./MediaCarousel.types";
import { MediaCarouselNavButton, MediaCarouselThumbnailStrip } from "./parts";
import { useMediaCarousel } from "./useMediaCarousel";

const DEFAULT_MEDIA_ASPECT_RATIO = 16 / 9;
const DEFAULT_THUMBNAIL_WIDTH = 88;

function MediaCarouselInner<T extends MediaCarouselItem>(props: MediaCarouselProps<T>) {
  const {
    items,
    index: controlledIndex,
    defaultIndex,
    onIndexChange,
    loop = false,
    preloadCount = 1,
    renderMedia,
    renderDetail,
    detailPlacement = "below-media",
    renderOverlay,
    renderNavButton,
    renderEmpty,
    mediaAspectRatio = DEFAULT_MEDIA_ASPECT_RATIO,
    mediaHeight,
    mediaMaxHeight,
    mediaFillWidth = false,
    fillHeight = false,
    showNavigation = true,
    enableKeyboardNavigation = true,
    accessibilityLabel = "Media carousel",
    previousLabel = "Previous item",
    nextLabel = "Next item",
    style,
    mediaStyle,
    detailStyle,
    testID,
  } = props;

  const {
    index,
    activeItem,
    itemCount,
    canGoPrevious,
    canGoNext,
    goToPrevious,
    goToNext,
    goToIndex,
    mountedItems,
    containerRef,
  } = useMediaCarousel<T>({
    items,
    index: controlledIndex,
    defaultIndex,
    onIndexChange,
    loop,
    preloadCount,
    enableKeyboardNavigation,
  });

  // Measured rather than derived from the window: the carousel is as likely to
  // sit in a split pane or beside a sidebar as it is to own the screen, and the
  // window overstates the room it actually has.
  const [frameSize, setFrameSize] = useState<Size | null>(null);
  const handleFrameLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setFrameSize((current) =>
      current !== null && current.width === width && current.height === height ? current : { width, height },
    );
  }, []);

  if (activeItem === undefined) {
    return (
      <View style={style} testID={testID}>
        {renderEmpty?.()}
      </View>
    );
  }

  const renderNavButtonFor = (direction: MediaCarouselNavDirection): ReactNode => {
    const isPrevious = direction === "previous";
    const onPress = isPrevious ? goToPrevious : goToNext;
    const disabled = isPrevious ? !canGoPrevious : !canGoNext;
    const defaultElement = (
      <MediaCarouselNavButton
        direction={direction}
        onPress={onPress}
        disabled={disabled}
        accessibilityLabel={isPrevious ? previousLabel : nextLabel}
      />
    );

    if (renderNavButton === undefined) return defaultElement;
    return renderNavButton({ direction, onPress, disabled, defaultElement });
  };

  const thumbnailPlacement = props.showThumbnails === true ? (props.thumbnailPlacement ?? "top") : "top";
  const thumbnailStrip =
    props.showThumbnails === true ?
      <MediaCarouselThumbnailStrip
        items={items}
        activeIndex={index}
        onSelect={goToIndex}
        renderThumbnail={props.renderThumbnail}
        thumbnailWidth={props.thumbnailWidth ?? DEFAULT_THUMBNAIL_WIDTH}
        thumbnailHeight={
          props.thumbnailHeight ?? Math.round((props.thumbnailWidth ?? DEFAULT_THUMBNAIL_WIDTH) / mediaAspectRatio)
        }
        style={[
          thumbnailPlacement === "top" ? styles.stripAboveMedia : styles.stripBelowMedia,
          props.thumbnailStripStyle,
        ]}
      />
    : null;

  const aspectRatio = activeItem.aspectRatio ?? mediaAspectRatio;
  const sizing = { fillHeight, mediaHeight, mediaMaxHeight, mediaFillWidth, aspectRatio };
  const frameStyle = resolveFrameStyle(sizing);
  const stageStyle = resolveStageStyle({ ...sizing, frame: frameSize });

  // A tab stop only when the arrow keys have somewhere to go — a focusable
  // region that does nothing is just an extra stop on the way past.
  const keyboardNavigable = enableKeyboardNavigation && itemCount > 1;

  // The detail area is placed either above or below the media block. Built once
  // here so the two placement branches render the identical node (same content,
  // same style) — only its position in the column changes.
  const detailArea =
    renderDetail !== undefined ?
      <View style={detailStyle}>{renderDetail({ item: activeItem, index, itemCount })}</View>
    : null;

  return (
    <View
      ref={containerRef}
      style={[styles.root, fillHeight && styles.rootFill, style]}
      testID={testID}
      role={keyboardNavigable ? "group" : undefined}
      accessibilityLabel={keyboardNavigable ? accessibilityLabel : undefined}
      tabIndex={keyboardNavigable ? 0 : undefined}
    >
      {detailPlacement === "above-media" && detailArea}

      {thumbnailPlacement === "top" && thumbnailStrip}

      {/* The frame reserves the space; the stage is the media itself, shaped to
          the item's aspect ratio and centred in whatever the frame turned out
          to be. Overlays sit on the stage, not the frame, so a badge lands in
          the corner of the picture rather than out in the letterbox. */}
      <View style={[styles.frame, frameStyle]} onLayout={needsMeasuredFrame(sizing) ? handleFrameLayout : undefined}>
        <View
          style={[styles.stage, stageStyle, mediaStyle]}
          testID={testID === undefined ? undefined : `${testID}-media`}
        >
          {mountedItems.map(({ item, index: mountedIndex, isActive }) => (
            // Keyed by item id so a slot that stays mounted across a navigation
            // keeps its media element — and therefore its already-fetched image
            // or buffered video — instead of remounting and refetching.
            <View key={item.id} style={[styles.slot, !isActive && styles.slotHidden]} aria-hidden={!isActive}>
              {renderMedia({ item, index: mountedIndex, isActive })}
            </View>
          ))}

          {/* pointerEvents is passed as a *prop*, not via the StyleSheet: on web,
              react-native-web only honours "box-none" through the prop path. In a
              StyleSheet it serialises the raw string to `pointer-events: box-none`,
              which is invalid CSS — the browser falls back to the inherited `auto`,
              so this full-cover overlay (and the nav row below) would swallow taps
              meant for the media beneath it (e.g. the video's play-to-reveal tap). */}
          <View style={styles.overlay} pointerEvents="box-none">
            {renderOverlay !== undefined && (
              <View style={styles.overlayContent} pointerEvents="box-none">
                {renderOverlay({
                  item: activeItem,
                  index,
                  itemCount,
                  goToPrevious,
                  goToNext,
                  canGoPrevious,
                  canGoNext,
                })}
              </View>
            )}

            {showNavigation && itemCount > 1 && (
              <View style={styles.navRow} pointerEvents="box-none">
                {renderNavButtonFor("previous")}
                {renderNavButtonFor("next")}
              </View>
            )}
          </View>
        </View>
      </View>

      {thumbnailPlacement === "bottom" && thumbnailStrip}

      {detailPlacement === "below-media" && detailArea}

      {/* "below-detail": the strip sits AFTER the detail area, so the order is
          media → detail → thumbnails (vs "bottom" which is media → thumbnails →
          detail). Lets the meta sit directly under the clip with the jump strip
          last. */}
      {thumbnailPlacement === "below-detail" && thumbnailStrip}
    </View>
  );
}

MediaCarouselInner.displayName = "MediaCarousel";

/**
 * Generic media carousel — a paged media area with a navigation overlay and an
 * optional detail area beneath it.
 *
 * The carousel owns navigation, layout and preloading; it renders no media
 * itself. Products supply `renderMedia` (snapshots, clips, anything else),
 * `renderDetail` for whatever belongs under the media, and `renderThumbnail`
 * when the jump-to-item strip is enabled.
 *
 * Only the active item and `preloadCount` neighbours on each side are ever
 * mounted, and the thumbnail strip is virtualised, so the number of media
 * elements alive at once stays bounded no matter how long `items` is.
 */
const MediaCarousel = MediaCarouselInner as <T extends MediaCarouselItem = MediaCarouselItem>(
  props: MediaCarouselProps<T>,
) => ReturnType<typeof MediaCarouselInner>;

export { MediaCarousel };

// ---------------------------------------------------------------------------
// Media sizing
// ---------------------------------------------------------------------------

interface Size {
  readonly width: number;
  readonly height: number;
}

interface FrameStyleOptions {
  readonly fillHeight: boolean;
  readonly mediaHeight: number | undefined;
  readonly mediaMaxHeight: number | undefined;
  readonly mediaFillWidth: boolean;
  readonly aspectRatio: number;
}

/**
 * How much room the media area claims. Only `fillHeight` takes its height from
 * the parent; the other modes derive it from the width the carousel is given,
 * which is what makes the default behaviour fluid across screen sizes.
 */
function resolveFrameStyle({ fillHeight, mediaHeight, mediaMaxHeight, aspectRatio }: FrameStyleOptions): ViewStyle {
  if (fillHeight) return { flex: 1, minHeight: 0 };
  // A plain height with NO aspectRatio keeps the frame's 100% width. This serves
  // both the centred fixed-height mode and the full-width mode (mediaFillWidth):
  // the difference is only in the *stage* — see resolveStageStyle, where
  // mediaFillWidth makes the stage fill this width instead of shrinking to the
  // picture's shape and centring.
  if (mediaHeight != null) return { height: mediaHeight };
  if (mediaMaxHeight != null) return { aspectRatio, maxHeight: mediaMaxHeight };
  return { aspectRatio };
}

interface StageStyleOptions extends FrameStyleOptions {
  readonly frame: Size | null;
}

const FILLS_FRAME: ViewStyle = { width: "100%", height: "100%" };

/**
 * The largest box of the requested shape that fits the frame, centred by the
 * frame's own flex alignment.
 *
 * Deliberately never reads the *measured* frame height outside `fillHeight`.
 * A measurement describes the item that produced it, so paging to a taller
 * ratio would size the new item against the old item's shorter frame and flash
 * an undersized box until the next layout. Everywhere else the height the frame
 * can offer is knowable without measuring — it is a prop, or it follows from
 * the width, and neither changes with the ratio.
 */
function resolveStageStyle({
  fillHeight,
  mediaHeight,
  mediaMaxHeight,
  mediaFillWidth,
  aspectRatio,
  frame,
}: StageStyleOptions): ViewStyle {
  // Full-width fixed-height surface: the stage takes the whole frame (100%
  // width × the fixed height) instead of shrinking to the picture's shape and
  // centring. Aspect mismatch is the media renderer's job (contain-fit → black
  // bars), so the surface itself lines up edge-to-edge with siblings.
  if (mediaFillWidth && mediaHeight != null) return FILLS_FRAME;
  // The frame already carries the ratio, so the stage is simply all of it.
  if (!needsMeasuredFrame({ fillHeight, mediaHeight, mediaMaxHeight })) return FILLS_FRAME;
  if (frame === null || frame.width <= 0) return FILLS_FRAME;

  const availableHeight = fillHeight ? frame.height : (mediaHeight ?? mediaMaxHeight);
  if (availableHeight == null || availableHeight <= 0) return FILLS_FRAME;

  const height = Math.min(availableHeight, frame.width / aspectRatio);
  return { width: height * aspectRatio, height };
}

/** Whether the stage has to be sized against a measurement at all. */
function needsMeasuredFrame({
  fillHeight,
  mediaHeight,
  mediaMaxHeight,
}: Pick<FrameStyleOptions, "fillHeight" | "mediaHeight" | "mediaMaxHeight">): boolean {
  return fillHeight || mediaHeight != null || mediaMaxHeight != null;
}

const styles = StyleSheet.create((theme) => ({
  root: {
    // Only the offset is set: the focus ring itself is left to the browser so
    // it keeps `:focus-visible` semantics — a ring for keyboard users, nothing
    // for a mouse click. A ring driven from onFocus state would show for both.
    outlineOffset: 2,
  },
  rootFill: {
    flex: 1,
    minHeight: 0,
  },
  stripAboveMedia: {
    marginBottom: theme.spacing.md,
  },
  stripBelowMedia: {
    marginTop: theme.spacing.md,
  },
  // Left transparent so the space around a letterboxed stage shows whatever the
  // carousel is sitting on rather than a slab of grey.
  frame: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  stage: {
    position: "relative",
    overflow: "hidden",
    borderRadius: theme.spacing.md,
    backgroundColor: theme.colors.borderCard,
  },
  slot: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  // Preloaded neighbours stay mounted but are invisible and inert.
  slotHidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  // pointerEvents lives on the JSX prop (see the overlay View), not here:
  // "box-none" is not valid CSS, so setting it via the StyleSheet breaks on web.
  overlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  overlayContent: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  navRow: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: theme.spacing.md,
  },
}));
